/**
 * File: timerUtils.ts
 * Path: apps/admin/src/shared/utils/timerUtils.ts
 * Description: The admin's one running timer: its shape, keeping it on this
 * device across reloads, and showing elapsed time as a clock.
 */

import type { EffortRecord } from "../services/effortService";

export const STORAGE_KEY = "idk-admin-timer";

export interface RunningTimer {
  record: EffortRecord;
  startedAt: number;
}

export const readStored = (): RunningTimer | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<RunningTimer>;
    const r = value.record;
    if (
      r && typeof r.id === "string" && typeof r.name === "string" && (r.kind === "lead" || r.kind === "client") &&
      typeof value.startedAt === "number"
    ) {
      return {
        record: { kind: r.kind, id: r.id, name: r.name, ...(typeof r.jobId === "string" && r.jobId ? { jobId: r.jobId } : {}) },
        startedAt: value.startedAt,
      };
    }
  } catch {
    // Storage can be blocked or hold something odd: no timer then.
  }
  return null;
};

export const writeStored = (timer: RunningTimer | null) => {
  try {
    if (timer) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(timer));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Not saved across a reload; the timer still runs on this page.
  }
};

/** mm:ss, or h:mm:ss past an hour. */
export const formatClock = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};
